/**
 * Geographic Map Component
 * Shows active users on a global map with animated dots (Shopify-style)
 * Uses Leaflet for reliable rendering without transpilation issues
 */

import React, { useMemo, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box, useColorModeValue, Text } from "@chakra-ui/react";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import LiftedTitleCard from "../../Dashboard/components/LiftedTitleCard";

// Fix for default marker icons in Leaflet with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// User location interface
export interface UserLocation {
  id: string;
  latitude: number;
  longitude: number;
  isActive: boolean; // true for active users (last 30 min), false for inactive
  country?: string;
  city?: string;
}

interface GeographicMapProps {
  userLocations?: UserLocation[];
  height?: string | number;
}

// Component to handle map resize - ensures Leaflet calculates size correctly
const MapResizeHandler: React.FC = () => {
  const map = useMap();
  
  useEffect(() => {
    // Use requestAnimationFrame for better timing
    const rafId = requestAnimationFrame(() => {
      map.invalidateSize();
    });
    
    // Also invalidate after a short delay
    const timer1 = setTimeout(() => {
      map.invalidateSize();
    }, 50);
    
    const timer2 = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    
    const timer3 = setTimeout(() => {
      map.invalidateSize();
    }, 500);
    
    // Also invalidate on window resize
    const handleResize = () => {
      map.invalidateSize();
    };
    window.addEventListener('resize', handleResize);
    
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);
  
  return null;
};

// Custom marker icon component - Shopify-style smooth animations
const CustomMarker: React.FC<{ user: UserLocation }> = ({ user }) => {
  const icon = L.divIcon({
    className: "custom-marker",
    html: `
      <div style="
        position: relative;
        width: ${user.isActive ? "14px" : "10px"};
        height: ${user.isActive ? "14px" : "10px"};
        border-radius: 50%;
        background-color: ${user.isActive ? "#06E363" : "#9CA3AF"};
        box-shadow: ${user.isActive 
          ? "0 0 0 0 rgba(6, 227, 99, 0.7), 0 0 0 0 rgba(6, 227, 99, 0.7), 0 0 0 0 rgba(6, 227, 99, 0.7)" 
          : "0 0 2px rgba(156, 163, 175, 0.3)"};
        transition: all 0.3s ease;
        animation: ${user.isActive ? "pulse-smooth 2s cubic-bezier(0.4, 0, 0.6, 1) infinite" : "none"};
        cursor: pointer;
      ">
        ${user.isActive ? `
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background-color: #06E363;
            opacity: 0.6;
            animation: ripple 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          "></div>
        ` : ""}
      </div>
      <style>
        @keyframes pulse-smooth {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(6, 227, 99, 0.7), 0 0 0 0 rgba(6, 227, 99, 0.7), 0 0 0 0 rgba(6, 227, 99, 0.7);
          }
          50% { 
            transform: scale(1.1);
            box-shadow: 0 0 0 4px rgba(6, 227, 99, 0), 0 0 0 8px rgba(6, 227, 99, 0), 0 0 0 12px rgba(6, 227, 99, 0);
          }
        }
        @keyframes ripple {
          0% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 0;
          }
        }
      </style>
    `,
    iconSize: [user.isActive ? 14 : 10, user.isActive ? 14 : 10],
    iconAnchor: [user.isActive ? 7 : 5, user.isActive ? 7 : 5],
  });

  return (
    <Marker
      position={[user.latitude, user.longitude] as [number, number]}
      // @ts-ignore - react-leaflet v4 types don't include icon prop
      icon={icon as any}
    >
      <Popup>
        <Box fontSize="sm">
          <Box fontWeight="bold">{user.city || "Unknown"}</Box>
          <Box color="gray.600">{user.country || "Unknown"}</Box>
        </Box>
      </Popup>
    </Marker>
  );
};

const GeographicMap: React.FC<GeographicMapProps> = ({
  userLocations = [],
  height = "500px",
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const containerRef = useRef<HTMLDivElement>(null);
  const [isContainerReady, setIsContainerReady] = useState(false);
  
  // Convert height to pixels if it's a string, ensure it's always a string
  const heightPx = typeof height === 'string' ? height : `${height}px`;

  // Mock data for demonstration - replace with real data from API
  const mockUserLocations: UserLocation[] = useMemo(() => {
    if (userLocations.length > 0) {
      return userLocations;
    }
    
    // Generate mock active user locations around the world
    return [
      // North America
      { id: "1", latitude: 40.7128, longitude: -74.0060, isActive: true, country: "USA", city: "New York" },
      { id: "2", latitude: 34.0522, longitude: -118.2437, isActive: true, country: "USA", city: "Los Angeles" },
      { id: "3", latitude: 41.8781, longitude: -87.6298, isActive: true, country: "USA", city: "Chicago" },
      { id: "4", latitude: 43.6532, longitude: -79.3832, isActive: true, country: "Canada", city: "Toronto" },
      { id: "5", latitude: 49.2827, longitude: -123.1207, isActive: false, country: "Canada", city: "Vancouver" },
      
      // Europe
      { id: "6", latitude: 51.5074, longitude: -0.1278, isActive: true, country: "UK", city: "London" },
      { id: "7", latitude: 48.8566, longitude: 2.3522, isActive: true, country: "France", city: "Paris" },
      { id: "8", latitude: 52.5200, longitude: 13.4050, isActive: true, country: "Germany", city: "Berlin" },
      { id: "9", latitude: 41.9028, longitude: 12.4964, isActive: false, country: "Italy", city: "Rome" },
      { id: "10", latitude: 40.4168, longitude: -3.7038, isActive: true, country: "Spain", city: "Madrid" },
      { id: "11", latitude: 59.3293, longitude: 18.0686, isActive: true, country: "Sweden", city: "Stockholm" },
      
      // Asia
      { id: "12", latitude: 35.6762, longitude: 139.6503, isActive: true, country: "Japan", city: "Tokyo" },
      { id: "13", latitude: 22.3193, longitude: 114.1694, isActive: true, country: "Hong Kong", city: "Hong Kong" },
      { id: "14", latitude: 1.3521, longitude: 103.8198, isActive: true, country: "Singapore", city: "Singapore" },
      { id: "15", latitude: 28.6139, longitude: 77.2090, isActive: false, country: "India", city: "New Delhi" },
      { id: "16", latitude: 31.2304, longitude: 121.4737, isActive: true, country: "China", city: "Shanghai" },
      
      // Australia
      { id: "17", latitude: -33.8688, longitude: 151.2093, isActive: true, country: "Australia", city: "Sydney" },
      { id: "18", latitude: -37.8136, longitude: 144.9631, isActive: false, country: "Australia", city: "Melbourne" },
      
      // South America
      { id: "19", latitude: -23.5505, longitude: -46.6333, isActive: true, country: "Brazil", city: "São Paulo" },
      { id: "20", latitude: -34.6037, longitude: -58.3816, isActive: false, country: "Argentina", city: "Buenos Aires" },
      
      // Middle East
      { id: "21", latitude: 25.2048, longitude: 55.2708, isActive: true, country: "UAE", city: "Dubai" },
      { id: "22", latitude: 31.7683, longitude: 35.2137, isActive: true, country: "Israel", city: "Jerusalem" },
    ];
  }, [userLocations]);

  const activeUsers = useMemo(() => 
    mockUserLocations.filter(user => user.isActive),
    [mockUserLocations]
  );

  // Ensure container has proper dimensions before rendering map
  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      
      // Set explicit dimensions
      container.style.height = heightPx;
      container.style.width = '100%';
      
      // Wait for next frame to ensure dimensions are applied
      requestAnimationFrame(() => {
        // Verify container has proper dimensions
        const rect = container.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setIsContainerReady(true);
        } else {
          // Retry after a short delay
          setTimeout(() => {
            setIsContainerReady(true);
          }, 100);
        }
      });
    }
  }, [heightPx]);

  const titleElement = (
    <Box
      w="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      position="relative"
      mt="-2px"
    >
      <Text
        fontSize={{ base: 'xs', sm: 'xs', md: 'sm' }}
        color="white"
        fontWeight='medium'
        lineHeight="1"
        whiteSpace="nowrap"
        fontFamily="'IBM Plex Sans', sans-serif"
        textTransform="uppercase"
        m="0"
        p="0"
        textAlign="center"
        w="100%"
      >
        Active Users Map
      </Text>
    </Box>
  );

  return (
    <LiftedTitleCard 
      title={titleElement}
      rightElement={<Box w={{ base: '12px', md: '20px', lg: '20px', xl: '20px' }} />}
      hideBottomSection={true}
      height={{ base: 'auto', md: 'auto' }}
      outerCardGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
      titlePadding={{ pl: { base: '12px', md: '20px', lg: '20px', xl: '20px' }, pr: { base: '12px', md: '20px', lg: '20px', xl: '20px' } }}
    >
      <Card bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
        <CardBody p={0}>
        <Box
          ref={containerRef}
          position="relative"
          height={heightPx}
          width="100%"
          borderRadius="md"
          overflow="hidden"
          border="1px solid"
          borderColor={borderColor}
          bg={useColorModeValue("gray.100", "gray.900")}
          minHeight={heightPx}
          id="leaflet-map-container"
          sx={{
            "& .leaflet-container": {
              height: `${heightPx} !important`,
              width: "100% !important",
              position: "relative !important",
              borderRadius: "md",
              fontFamily: "'Urbanist', sans-serif",
            },
            // Constrain all Leaflet panes to reasonable z-index values
            "& .leaflet-pane": {
              zIndex: "400 !important",
            },
            "& .leaflet-map-pane": {
              zIndex: "400 !important",
            },
            "& .leaflet-tile-pane": {
              zIndex: "200 !important",
            },
            "& .leaflet-overlay-pane": {
              zIndex: "400 !important",
            },
            "& .leaflet-shadow-pane": {
              zIndex: "500 !important",
            },
            "& .leaflet-marker-pane": {
              zIndex: "600 !important",
            },
            "& .leaflet-tooltip-pane": {
              zIndex: "650 !important",
            },
            "& .leaflet-popup-pane": {
              zIndex: "700 !important",
            },
            "& .leaflet-control": {
              zIndex: "800 !important",
            },
            "& .leaflet-top, & .leaflet-bottom": {
              zIndex: "1000 !important",
            },
            "& .leaflet-control-zoom": {
              border: "none !important",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15) !important",
              borderRadius: "6px !important",
            },
            "& .leaflet-control-zoom a": {
              backgroundColor: "white !important",
              color: "#333 !important",
              border: "none !important",
              borderRadius: "6px !important",
              width: "32px !important",
              height: "32px !important",
              lineHeight: "32px !important",
              transition: "all 0.2s ease !important",
            },
            "& .leaflet-control-zoom a:hover": {
              backgroundColor: "#f5f5f5 !important",
              transform: "scale(1.05) !important",
            },
            "& .leaflet-popup-content-wrapper": {
              borderRadius: "8px !important",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15) !important",
            },
          }}
        >
          {typeof window !== 'undefined' && isContainerReady && (
            <MapContainer
              // @ts-ignore - react-leaflet v4 types issue
              center={[20, 0] as [number, number]}
              zoom={2}
              minZoom={2}
              maxZoom={2}
              style={{ 
                height: heightPx, 
                width: "100%",
                position: "relative",
              }}
              scrollWheelZoom={false}
              doubleClickZoom={false}
              zoomControl={false}
              attributionControl={true}
              dragging={true}
              touchZoom={false}
              boxZoom={false}
              key="leaflet-map"
              preferCanvas={true}
            >
              <MapResizeHandler />
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                // @ts-ignore - react-leaflet v4 types issue
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                maxZoom={19}
                subdomains="abcd"
              />
              {mockUserLocations
                .filter(user => user.isActive)
                .map((user) => (
                  <CustomMarker key={user.id} user={user} />
                ))}
            </MapContainer>
          )}
        </Box>
      </CardBody>
    </Card>
    </LiftedTitleCard>
  );
};

export default GeographicMap;
